import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword } from '../services/auth';
import { useAuth } from '../contexts/AuthContext';
import DescriptionIcon from '@mui/icons-material/Description';
import GoogleIcon from '@mui/icons-material/Google';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  Typography,
  Box
} from '@mui/material';
import './Login.css';
import EmailVerificationReminder from './EmailVerificationReminder';

const PASSWORD_REQUIREMENTS = [
  'At least 8 characters long',
  'Contains at least one uppercase letter',
  'Contains at least one lowercase letter',
  'Contains at least one number',
  'Contains at least one special character'
];

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, redirectToDashboard, refreshUser } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      if (!user.emailVerified) {
        setShowVerificationMessage(true);
      } else {
        const from = (location.state as any)?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }
    }
  }, [user, navigate, location]);

  const validatePassword = (pass: string): boolean => {
    const hasMinLength = pass.length >= 8;
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasLowerCase = /[a-z]/.test(pass);
    const hasNumber = /[0-9]/.test(pass);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pass);
    
    return hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
  };

  const getPasswordStrength = (pass: string): number => {
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (/[A-Z]/.test(pass)) strength++;
    if (/[a-z]/.test(pass)) strength++;
    if (/[0-9]/.test(pass)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pass)) strength++;
    return strength;
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to sign in with Google');
      console.error('Sign in error:', err);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowVerificationMessage(false);

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (isSignUp) {
      if (!validatePassword(password)) {
        setError('Password does not meet requirements');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    try {
      setLoading(true);
      if (isSignUp) {
        const user = await signUpWithEmail(email, password);
        setShowVerificationMessage(true);
        await refreshUser();
      } else {
        const user = await signInWithEmail(email, password);
        if (!user.emailVerified) {
          setShowVerificationMessage(true);
        }
        await refreshUser();
      }
    } catch (error: any) {
      let errorMessage = 'Authentication failed';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many attempts. Please try again later.';
      }
      setError(errorMessage);
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetSuccess(false);

    if (!resetEmail) {
      setResetError('Please enter your email address');
      return;
    }

    try {
      setResetLoading(true);
      await resetPassword(resetEmail);
      setResetSuccess(true);
      setResetEmail('');
    } catch (error: any) {
      let errorMessage = 'Failed to send reset email';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many attempts. Please try again later.';
      }
      setResetError(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setResetEmail('');
    setResetError(null);
    setResetSuccess(false);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <DescriptionIcon className="login-icon" />
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <h1>Resume Doctor</h1>
          </Link>
          <p className="tagline">Optimize your resume for your dream job</p>
        </div>
        
        <div className="login-content">
          <div className="login-actions">
            {showVerificationMessage && (
              <EmailVerificationReminder />
            )}
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleEmailAuth} className="email-form">
              <div className="form-group">
                <EmailIcon className="input-icon" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  required
                />
              </div>
              <div className="form-group">
                <LockIcon className="input-icon" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                />
              </div>
              {!isSignUp && (
                <button
                  type="button"
                  onClick={() => setShowResetModal(true)}
                  className="forgot-password-link"
                >
                  Forgot password?
                </button>
              )}
              {isSignUp && (
                <>
                  <div className="form-group">
                    <LockIcon className="input-icon" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                      required
                    />
                  </div>
                  <div className="password-requirements">
                    <h4>Password Requirements:</h4>
                    <ul>
                      {PASSWORD_REQUIREMENTS.map((requirement, index) => (
                        <li 
                          key={index}
                          className={getPasswordStrength(password) > index ? 'met' : ''}
                        >
                          {requirement}
                        </li>
                      ))}
                    </ul>
                    <div className="password-strength">
                      <div 
                        className="strength-bar"
                        style={{ width: `${(getPasswordStrength(password) / 5) * 100}%` }}
                      />
                    </div>
                  </div>
                </>
              )}
              <button 
                type="submit" 
                className="email-auth-button"
                disabled={loading}
              >
                {loading ? 'Please wait...' : isSignUp ? 'Sign Up' : 'Sign In'}
              </button>
            </form>

            <div className="divider">
              <span>or</span>
            </div>

            <button 
              onClick={handleGoogleSignIn} 
              className="google-sign-in"
              disabled={loading}
            >
              <GoogleIcon /> Continue with Google
            </button>

            <p className="toggle-auth">
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button 
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError(null);
                  setPassword('');
                  setConfirmPassword('');
                }}
                className="toggle-button"
              >
                {isSignUp ? 'Sign In' : 'Sign Up'}
              </button>
            </p>

            <p className="terms">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      <Dialog 
        open={showResetModal} 
        onClose={handleCloseResetModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {resetSuccess ? (
            <Box sx={{ py: 2 }}>
              <Typography>
                Password reset email sent! Please check your inbox and follow the instructions to reset your password.
              </Typography>
            </Box>
          ) : (
            <form onSubmit={handleResetPassword}>
              <Box sx={{ py: 2 }}>
                <Typography sx={{ mb: 2 }}>
                  Enter your email address and we'll send you a link to reset your password.
                </Typography>
                <TextField
                  fullWidth
                  label="Email address"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  error={!!resetError}
                  helperText={resetError}
                  disabled={resetLoading}
                  sx={{ mb: 2 }}
                />
              </Box>
              <DialogActions>
                <Button onClick={handleCloseResetModal} disabled={resetLoading}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={resetLoading}
                  sx={{ 
                    bgcolor: '#4F46FD',
                    '&:hover': {
                      bgcolor: '#4338ca'
                    }
                  }}
                >
                  {resetLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </DialogActions>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login; 